/**
 * Horizon Darts — header navigation interactivity.
 *
 * Progressive enhancement only: with this script disabled or slow to load,
 * every top-level link, mega-menu link, and mobile accordion row (native
 * <details>/<summary>) still works.
 *
 * Top-level nav items that HAVE a dropdown are anchors, not buttons, so a
 * click goes straight to that category's "All" collection page. The panel is
 * a hover/focus disclosure layered on top — this file must therefore never
 * preventDefault() on a trigger click, or those links would stop navigating.
 *
 * Responsibilities:
 *   1. open/close the mobile nav <dialog>,
 *   2. open mega-menu panels on hover-intent and keyboard focus, and close
 *      them on outside click, Escape, or focus leaving the header,
 *   3. wire the live-chat placeholder buttons.
 */

const DESKTOP_NAV_QUERY = '(min-width: 990px)';

/**
 * @param {HTMLElement} header
 */
function initHeader(header) {
  const drawer = header.querySelector('[data-nav-drawer]');
  const drawerOpenBtn = header.querySelector('[data-drawer-open]');
  const drawerCloseBtns = header.querySelectorAll('[data-drawer-close]');
  const triggers = Array.from(header.querySelectorAll('[data-mega-trigger]'));

  const panelFor = (trigger) => {
    const id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  };

  /**
   * @param {Element} [except]
   */
  const closeAllMegaMenus = (except) => {
    triggers.forEach((trigger) => {
      if (trigger === except) return;
      trigger.setAttribute('aria-expanded', 'false');
      panelFor(trigger)?.classList.remove('is-open');
    });
  };

  const openMegaMenu = (trigger) => {
    const panel = panelFor(trigger);
    if (!panel) return;
    closeAllMegaMenus(trigger);
    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');
  };

  const closeMegaMenu = (trigger) => {
    trigger.setAttribute('aria-expanded', 'false');
    panelFor(trigger)?.classList.remove('is-open');
  };

  triggers.forEach((trigger) => {
    const item = trigger.closest('.site-nav__item');
    /** @type {number | undefined} */
    let closeTimer;

    // NOTE: no click handler on the trigger itself. These are anchors and a
    // click must be allowed to navigate to the category's "All" page.

    item?.addEventListener('mouseenter', () => {
      if (!window.matchMedia(DESKTOP_NAV_QUERY).matches) return;
      window.clearTimeout(closeTimer);
      openMegaMenu(trigger);
    });

    item?.addEventListener('mouseleave', () => {
      if (!window.matchMedia(DESKTOP_NAV_QUERY).matches) return;
      closeTimer = window.setTimeout(() => closeMegaMenu(trigger), 150);
    });

    // Keyboard: focusing the trigger reveals its panel so the links inside
    // are reachable by continuing to Tab.
    trigger.addEventListener('focus', () => {
      if (!window.matchMedia(DESKTOP_NAV_QUERY).matches) return;
      openMegaMenu(trigger);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openTrigger = triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
    if (openTrigger) {
      closeMegaMenu(openTrigger);
      /** @type {HTMLElement} */ (openTrigger).focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node)) return;
    if (header.contains(event.target)) return;
    closeAllMegaMenus();
  });

  header.addEventListener('focusout', (event) => {
    const nextFocus = event.relatedTarget;
    if (nextFocus instanceof Node && header.contains(nextFocus)) return;
    closeAllMegaMenus();
  });

  window.matchMedia(DESKTOP_NAV_QUERY).addEventListener('change', (event) => {
    if (!event.matches) closeAllMegaMenus();
  });

  // Mobile drawer (native <dialog> — showModal() gives a focus trap,
  // Escape-to-close, and top-layer stacking for free).
  if (drawer instanceof HTMLDialogElement) {
    drawerOpenBtn?.addEventListener('click', () => {
      closeAllMegaMenus();
      drawer.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
    });

    const closeDrawer = () => drawer.close();
    drawerCloseBtns.forEach((btn) => btn.addEventListener('click', closeDrawer));

    drawer.addEventListener('cancel', () => {
      document.documentElement.removeAttribute('scroll-lock');
    });

    drawer.addEventListener('close', () => {
      document.documentElement.removeAttribute('scroll-lock');
    });

    // A click on the ::backdrop lands on the <dialog> element itself.
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) closeDrawer();
    });

    window.matchMedia(DESKTOP_NAV_QUERY).addEventListener('change', (event) => {
      if (event.matches && drawer.open) closeDrawer();
    });
  }

  // Live chat placeholder — no provider is connected yet.
  header.querySelectorAll('[data-chat-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const chat = /** @type {any} */ (window).Shopify?.Chat;
      if (chat && typeof chat.open === 'function') {
        chat.open();
        return;
      }
      console.info(
        '[Horizon Darts] Live chat placeholder: no chat provider is connected yet. ' +
          'Install Shopify Inbox (or wire another provider) and update the click handler ' +
          'in assets/header-nav.js ([data-chat-trigger]) to activate this button.'
      );
    });
  });
}

document.querySelectorAll('[data-site-header]').forEach((header) => {
  if (header instanceof HTMLElement) initHeader(header);
});